/* @flow */
import React, { useState, useEffect } from 'react';
import styles from './GlobalHeader.module.pcss';
import { Popover, Button, Card, Icon, Typography, Input, Upload, Row, Col, Form } from 'antd';
import { useImmer } from 'use-immer';
import { FormComponentProps } from 'antd/es/form';
import { useShallowEqualSelector } from '@/utils/redux';
import { isPhoneValid, isEmailValid } from '@/utils/validation';

const { Paragraph } = Typography;
const { Item } = Form;

type Props = {
  auth: {
    profile: ProfileT,
    isSaveSupportInfo: boolean,
    saveSupportInfoStatus: string,
  },
  form: FormComponentProps,
  saveSupportInfo: ({ formData: Array<string>, token: string }) => Promise<any>,
};

type State = {
  fileList: Array<FileList>,
};

const Support = (props: Props) => {
  const {
    auth: { profile, isSaveSupportInfo, saveSupportInfoStatus },
    form: { getFieldDecorator, validateFields },
    saveSupportInfo,
  } = props;

  const { token } = useShallowEqualSelector(({ auth }) => ({
    token: auth.profile.token,
  }));

  const userName = `${profile.first_name} ${profile.last_name}`;
  const responsiveCol = { xxl: 22, xl: 22, lg: 22, md: 22, sm: 22, xs: 22 };
  const responsiveColClose = { xxl: 2, xl: 2, lg: 2, md: 2, sm: 2, xs: 2 };

  const [showContactUsBtn, setShowContactUsBtn] = useState(true);
  const [showCloseBtn, setShowCloseBtn] = useState(false);
  const [showSupportWindow, setShowSupportWindow] = useState(false);
  const [showSupportSuccess, setShowSupportSuccess] = useState(false);
  const [charCount, setCharCount] = React.useState(0);

  const [state, setState] = useImmer<State>({
    fileList: [],
  });

  useEffect(() => {
    if (saveSupportInfoStatus == 'CREATED') {
      setShowContactUsBtn(false);
      setShowCloseBtn(true);
      setShowSupportWindow(false);
      setShowSupportSuccess(true);
    } else {
      setShowContactUsBtn(true);
      setShowCloseBtn(false);
      setShowSupportWindow(false);
      setShowSupportSuccess(false);
      setCharCount(0);
    }
  }, [saveSupportInfoStatus]);

  const uploadProps = {
    onRemove: (file) => {
      setState((draft) => {
        const index = draft.fileList.indexOf(file);
        const newFileList = draft.fileList.slice();
        newFileList.splice(index, 1);
        draft.fileList = newFileList;
      });
    },
    beforeUpload: (file) => {
      setState((draft) => {
        draft.fileList = draft.fileList.slice(1);
        draft.fileList.push(file);
      });
      return false;
    },
    multiple: false,
    fileList: state.fileList,
  };

  const showSupportPopup = () => {
    setShowContactUsBtn(false);
    setShowCloseBtn(true);
    setShowSupportWindow(true);
    setShowSupportSuccess(false);
  };

  const hideSupportPopup = () => {
    setShowSupportSuccess(false);
    setShowSupportWindow(false);
    setShowCloseBtn(false);
    setShowContactUsBtn(true);
    setCharCount(0);
    setState((draft) => {
      draft.fileList = draft.fileList.slice(1);
    });
  };

  const validateContact = (rule, value, callback) => {
    if (value && !isPhoneValid(value)) {
      if (!isEmailValid(value)) {
        callback('Please specify valid email or phone #');
      } else {
        callback();
      }
    } else {
      callback();
    }
  };

  const onSubmitSupportInfo = (e) => {
    e.preventDefault();
    const { fileList } = state;
    const formData: any = new FormData();
    validateFields((err, values) => {
      if (!err) {
        fileList.forEach((file, i) => {
          formData.append('file', file);
        });
        formData.append('contact', values.contact);
        formData.append('message', values.message);
        saveSupportInfo({ formData, token });
      }
    });
  };

  const content = (
    <div>
      <Row>
        <Col span={24}>
          <Card
            bodyStyle={{ background: 'rgba(0, 0, 0, 0.025)', height: '565px' }}
            display-if={showCloseBtn && showSupportWindow}
          >
            <Form onSubmit={onSubmitSupportInfo}>
              <div>
                <Row>
                  <Col {...responsiveCol}>
                    <h1>CONTACT US</h1>
                  </Col>
                  <Col {...responsiveColClose}>
                    <Icon type="close" onClick={hideSupportPopup} />
                  </Col>
                </Row>
              </div>
              <Paragraph style={{ marginBottom: '0px' }}>
                <b>{userName}</b>
              </Paragraph>
              <Item label="Best Method to Contact:" colon={false} style={{ marginBottom: '5px' }}>
                {getFieldDecorator('contact', {
                  rules: [
                    {
                      required: true,
                      message: 'Required',
                    },
                    {
                      validator: validateContact,
                    },
                  ],
                })(<Input size="large" placeholder="Email or Phone #" />)}
              </Item>
              <Item label="Please let us know how we can help you:" style={{ marginBottom: '5px' }}>
                {getFieldDecorator('message', {
                  rules: [
                    {
                      required: true,
                      message: 'Required',
                    },
                    {
                      type: 'string',
                    },
                  ],
                })(
                  <Input.TextArea
                    size="large"
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    maxLength={1024}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    style={{ resize: 'none' }}
                  />
                )}
              </Item>
              <div>
                <Paragraph style={{ marginLeft: '80%', fontSize: '13px' }}>{charCount}/1024</Paragraph>
              </div>
              <Item style={{ marginBottom: '15px' }}>
                <Upload.Dragger
                  {...uploadProps}
                  accept=".xls, .xlsx, .csv, .pdf, .txt, .doc, .docx, image/png, image/jpeg"
                  id="help-data-upload"
                >
                  <p className={styles.antUploadDragIcon}>
                    <Icon type="cloud-upload" />
                  </p>
                  <p className={styles.antUploadText}>Click or drag file to this area to upload</p>
                  <p className={styles.antUploadHint}>
                    Supports individual uploads of pdf, image, doc, and docx files.
                  </p>
                </Upload.Dragger>
              </Item>
              <Item wrapperCol={{ span: 12, offset: 9 }} style={{ marginBottom: '5px' }}>
                <Button type="primary" htmlType="submit" loading={isSaveSupportInfo}>
                  Submit
                </Button>
              </Item>
            </Form>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card
            bodyStyle={{ background: 'rgba(0, 0, 0, 0.025)', textAlign: 'center' }}
            display-if={showCloseBtn && showSupportSuccess}
          >
            <div>
              <Row>
                <Col {...responsiveCol}></Col>
                <Col {...responsiveColClose}>
                  <Icon type="close" onClick={hideSupportPopup} />
                </Col>
              </Row>
            </div>
            <p style={{ fontSize: 'initial' }}>
              <b>Thank you for your feedback.</b>
            </p>
            <p>
              <b>A team member will be in </b>
              <br />
              <b>contact with you shortly.</b>
            </p>
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <div className={styles.bottom}>
      <Popover
        placement="topRight"
        content={content}
        trigger="click"
        overlayClassName={styles.supportPopup}
        data-container="body"
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        visible={showCloseBtn}
        destroyTooltipOnHide={true}
      >
        <Button
          shape="circle"
          size="large"
          onClick={showSupportPopup}
          className={styles.contactUsBtn}
          display-if={showContactUsBtn}
        >
          <span>CONTACT</span>
          <span>US</span>
        </Button>
      </Popover>
    </div>
  );
};

export default Form.create()(Support);
